import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import API from '../Network/API';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const SUPPORT_EMAIL = 'info@xller.com';
const MESSAGE_MAX = 5000;

// Values must match HelpController::CATEGORIES on the backend; labels are translated.
const STUDENT_TOPICS = ['General', 'Booking & lessons', 'Payments & refunds', 'Account & login', 'Technical problem'];
const TEACHER_TOPICS = ['General', 'Courses & teaching', 'Earnings & withdrawals', 'Account & login', 'Technical problem'];
const TOPIC_KEYS = {
    'General': 'topic_general',
    'Booking & lessons': 'topic_booking',
    'Payments & refunds': 'topic_payments',
    'Account & login': 'topic_account',
    'Technical problem': 'topic_technical',
    'Courses & teaching': 'topic_courses',
    'Earnings & withdrawals': 'topic_earnings',
};

// Shared by the student (/home/help) and teacher (/dashboard/help) dashboards.
const Help = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { user } = useSelector((state) => state.authReducer);
    const isTeacher = user?.role === 'teacher';
    const prefix = location.pathname.startsWith('/app') ? '/app' : '';
    const topics = isTeacher ? TEACHER_TOPICS : STUDENT_TOPICS;

    const [loading, setLoad] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [data, setData] = useState();
    const [expandedId, setExpandedId] = useState(null);
    const [topic, setTopic] = useState('General');
    const [message, setMessage] = useState('');

    // Guide entries: question, answer and an optional shortcut into the dashboard.
    const guide = isTeacher ? [
        { icon: 'fa-chalkboard-user', q: 'help.t_q_create', a: 'help.t_a_create', link: `${prefix}/dashboard/my-course/create`, linkLabel: 'help.t_l_create' },
        { icon: 'fa-calendar-clock', q: 'help.t_q_schedule', a: 'help.t_a_schedule', link: `${prefix}/dashboard/settings/schedules`, linkLabel: 'help.t_l_schedule' },
        { icon: 'fa-video', q: 'help.t_q_live', a: 'help.t_a_live', link: `${prefix}/dashboard/all-enrollment-courses`, linkLabel: 'help.t_l_live' },
        { icon: 'fa-wallet', q: 'help.t_q_earnings', a: 'help.t_a_earnings', link: `${prefix}/dashboard/earnings`, linkLabel: 'help.t_l_earnings' },
        { icon: 'fa-money-bill-transfer', q: 'help.t_q_withdraw', a: 'help.t_a_withdraw', link: `${prefix}/dashboard/settings/change-bank-info`, linkLabel: 'help.t_l_withdraw' },
        { icon: 'fa-message-lines', q: 'help.t_q_messages', a: 'help.t_a_messages', link: `${prefix}/dashboard/chat`, linkLabel: 'help.t_l_messages' },
    ] : [
        { icon: 'fa-calendar-check', q: 'help.s_q_book', a: 'help.s_a_book', link: `${prefix}/all-courses`, linkLabel: 'help.s_l_book' },
        { icon: 'fa-video', q: 'help.s_q_join', a: 'help.s_a_join', link: `${prefix}/home/my-lessons`, linkLabel: 'help.s_l_join' },
        { icon: 'fa-credit-card', q: 'help.s_q_pay', a: 'help.s_a_pay' },
        { icon: 'fa-rotate-left', q: 'help.s_q_refund', a: 'help.s_a_refund', link: "/terms", linkLabel: 'help.s_l_refund' },
        { icon: 'fa-message-lines', q: 'help.s_q_message', a: 'help.s_a_message', link: `${prefix}/home/chat`, linkLabel: 'help.s_l_message' },
        { icon: 'fa-user-gear', q: 'help.s_q_account', a: 'help.s_a_account', link: `${prefix}/home/settings/profile`, linkLabel: 'help.s_l_account' },
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        const form = e.target;

        setLoad(true)
        const fd = new FormData();
        fd.append('category', topic);
        fd.append('subject', form.subject.value);
        fd.append('message', message);
        API.post("/v1/oauth/save_help_request", fd, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(() => {
            setLoad(false)
            toast.success(t('help.request_submitted'));
            fetchHelp();
            form.reset();
            setMessage('');
            setTopic('General');
        }).catch((err) => {
            setLoad(false)
            const status = err?.response?.status;
            toast.error(status === 429 ? t('help.too_many_requests') : (err?.response?.data?.message || t('help.could_not_submit')));
        });
    };

    const fetchHelp = () => {
        API.get(`/v1/oauth/get_help_request`)
            .then((response) => {
                setData(response.data.data)
                setListLoading(false)
            }).catch(() => {
                setListLoading(false)
            });
    }

    useEffect(() => {
        fetchHelp();
    }, []);

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(SUPPORT_EMAIL);
            toast.success(t('help.email_copied'));
        } catch (e) {
            window.location.href = `mailto:${SUPPORT_EMAIL}`;
        }
    };

    const statusBadge = (item) => {
        if (item?.reply) return <span className="status-badge ok">{t("help.replied")}</span>;
        if (Number(item?.read) === 0) return <span className="status-badge pending">{t("help.pending")}</span>;
        return <span className="status-badge muted">{t("help.read")}</span>;
    };

    return (
        <div className="help-page">
            <div className="help-hero">
                <div>
                    <h4 className="help-hero-title">{t("help.help_support")}</h4>
                    <p className="help-hero-sub">{isTeacher ? t("help.hero_sub_teacher") : t("help.hero_sub_student")}</p>
                </div>
                <div className="help-contact">
                    <span className="help-contact-icon"><i className="fa-light fa-envelope" /></span>
                    <div className="help-contact-text">
                        <span>{t("help.email_us")}</span>
                        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                    </div>
                    <button type="button" className="help-copy" onClick={copyEmail} aria-label={t("help.copy_email")} title={t("help.copy_email")}>
                        <i className="fa-light fa-copy" />
                    </button>
                </div>
            </div>

            <section className="help-section">
                <h5 className="help-section-title">{t("help.guide_title")}</h5>
                <div className="help-guide">
                    {guide.map((item) => (
                        <details className="help-faq" key={item.q}>
                            <summary>
                                <span className="help-faq-icon"><i className={`fa-light ${item.icon}`} /></span>
                                <span className="help-faq-q">{t(item.q)}</span>
                                <i className="fa-light fa-chevron-down help-faq-chevron" />
                            </summary>
                            <div className="help-faq-a">
                                <p>{t(item.a)}</p>
                                {item.link &&
                                    <Link to={item.link} className="help-faq-link">
                                        {t(item.linkLabel)}<i className="fa-light fa-arrow-right" />
                                    </Link>
                                }
                            </div>
                        </details>
                    ))}
                </div>
            </section>

            <section className="help-section" id="help-form">
                <div className="dash-card help-form-card">
                    <h5 className="help-section-title mb-1">{t("help.new_request")}</h5>
                    <p className="text-muted mb-3" style={{ fontSize: 14 }}>{t("help.need_a_hand_email", { email: SUPPORT_EMAIL })}</p>
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-5">
                                <label htmlFor="help-topic" className="form-label">{t("help.topic")}</label>
                                <select id="help-topic" className="form-select" value={topic} onChange={(e) => setTopic(e.target.value)}>
                                    {topics.map((value) => <option key={value} value={value}>{t(`help.${TOPIC_KEYS[value]}`)}</option>)}
                                </select>
                            </div>
                            <div className="col-md-7">
                                <label htmlFor="subject" className="form-label">{t("help.subject")}</label>
                                <input type="text" id="subject" name="subject" required maxLength={150} className="form-control" placeholder={t("help.subject_placeholder")} />
                            </div>
                            <div className="col-12">
                                <label htmlFor="message" className="form-label">{t("help.message")}</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    required
                                    rows={5}
                                    maxLength={MESSAGE_MAX}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="form-control"
                                    placeholder={t("help.message_placeholder")}
                                />
                                <div className="help-form-meta">
                                    <span>{t("help.reply_by_email")}</span>
                                    <span>{message.length}/{MESSAGE_MAX}</span>
                                </div>
                            </div>
                        </div>
                        <button type="submit" disabled={loading} className="btn btn-login input-hight-44 fw-bold mt-3 px-4">
                            {loading ?
                                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{t("help.sending")}</>
                                :
                                <><i className="fa-light fa-paper-plane me-2" />{t('help.send_request')}</>
                            }
                        </button>
                    </form>
                </div>
            </section>

            <div className="dashboard-title">
                <h4>{t("help.your_requests")}</h4>
            </div>
            <div className="col-12">
                {listLoading &&
                    <p className="text-muted py-3">{t("help.loading_requests")}</p>
                }
                {!listLoading && !(data?.length > 0) &&
                    <div className="dash-card text-center text-muted py-5">
                        <i className="fa-light fa-messages-question d-block mb-2" style={{ fontSize: 28 }} />
                        {t("help.no_requests")}
                    </div>
                }
                {!listLoading && data?.length > 0 &&
                    <div className="table-responsive custom-table">
                        <table className="table table-nowrap mb-0">
                            <thead>
                                <tr>
                                    <th>{t("help.ticket")}</th>
                                    <th>{t("help.date")}</th>
                                    <th>{t("help.subject")}</th>
                                    <th>{t("help.status")}</th>
                                    <th>{t("help.action")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.map((item, index) => (
                                    <React.Fragment key={item?.id ?? index}>
                                        <tr>
                                            <td data-label={t("help.ticket")}>#{item?.id}</td>
                                            <td data-label={t("help.date")}>{new Date(item?.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                            <td data-label={t("help.subject")}>{item?.subject}</td>
                                            <td data-label={t("help.status")}>{statusBadge(item)}</td>
                                            <td className="td-actions">
                                                <button type="button" onClick={() => setExpandedId(expandedId === item?.id ? null : item?.id)} className="row-action-btn">
                                                    <i className={`fa-light ${expandedId === item?.id ? 'fa-chevron-up' : 'fa-eye'}`} />
                                                    {expandedId === item?.id ? t('help.hide') : t('help.view')}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedId === item?.id &&
                                            <tr className="help-detail-row">
                                                <td colSpan={5}>
                                                    <div className="help-detail">
                                                        <p className="help-detail-label">{t("help.your_message")}</p>
                                                        <p className="help-detail-text">{item?.message}</p>
                                                        {item?.reply ?
                                                            <>
                                                                <p className="help-detail-label mt-3"><i className="fa-light fa-reply me-1" />{t("help.xller_team_replied")}</p>
                                                                <p className="help-detail-text help-detail-reply">{item?.reply}</p>
                                                            </>
                                                            :
                                                            <p className="text-muted mb-0" style={{ fontSize: 13 }}>{t("help.no_reply_yet")}</p>
                                                        }
                                                    </div>
                                                </td>
                                            </tr>
                                        }
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                }
            </div>
        </div>
    );
};

export default Help;
